import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AddPTORequestModal = ({ employees, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    employeeId: '',
    ptoType: 'Vacation',
    startDate: '',
    endDate: '',
    reason: '',
    coverageArrangement: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const calcDays = () => {
    if (!form?.startDate || !form?.endDate) return 0;
    const start = new Date(form.startDate);
    const end = new Date(form.endDate);
    if (end < start) return 0;
    let count = 0;
    const cur = new Date(start);
    while (cur <= end) {
      const day = cur?.getDay();
      if (day !== 0 && day !== 6) count++;
      cur?.setDate(cur?.getDate() + 1);
    }
    return count;
  };

  const days = calcDays();

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!form?.employeeId) { setError('Please select an employee.'); return; }
    if (!form?.startDate || !form?.endDate) { setError('Please select start and end dates.'); return; }
    if (new Date(form.endDate) < new Date(form.startDate)) { setError('End date must be after start date.'); return; }
    if (days === 0) { setError('No working days in selected range.'); return; }
    setSubmitting(true);
    try {
      await onSubmit?.({ ...form, daysRequested: days });
      onClose?.();
    } catch (err) {
      setError(err?.message ?? 'Failed to submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl elevation-lg w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="CalendarPlus" size={20} className="text-primary" />
            <h2 className="text-base font-heading font-semibold text-foreground">New PTO Request</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
              <Icon name="AlertCircle" size={14} />
              {error}
            </div>
          )}

          <div>
            <label className="text-xs font-caption text-muted-foreground mb-1 block">Employee *</label>
            <select
              value={form?.employeeId}
              onChange={(e) => setForm({ ...form, employeeId: e?.target?.value })}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
              required
            >
              <option value="">Select employee...</option>
              {employees?.map((emp) => (
                <option key={emp?.id} value={emp?.id}>{emp?.name} ({emp?.employeeId})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-caption text-muted-foreground mb-1 block">PTO Type *</label>
            <select
              value={form?.ptoType}
              onChange={(e) => setForm({ ...form, ptoType: e?.target?.value })}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
            >
              {['Vacation', 'Sick', 'Personal', 'Bereavement', 'Other']?.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-caption text-muted-foreground mb-1 block">Start Date *</label>
              <input
                type="date"
                value={form?.startDate}
                onChange={(e) => setForm({ ...form, startDate: e?.target?.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
                required
              />
            </div>
            <div>
              <label className="text-xs font-caption text-muted-foreground mb-1 block">End Date *</label>
              <input
                type="date"
                value={form?.endDate}
                onChange={(e) => setForm({ ...form, endDate: e?.target?.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
                required
              />
            </div>
          </div>

          {days > 0 && (
            <div className="flex items-center gap-2 p-3 bg-primary/5 text-primary rounded-lg text-sm">
              <Icon name="Info" size={14} />
              <span>{days} working day{days !== 1 ? 's' : ''} requested</span>
            </div>
          )}

          <div>
            <label className="text-xs font-caption text-muted-foreground mb-1 block">Reason</label>
            <textarea
              value={form?.reason}
              onChange={(e) => setForm({ ...form, reason: e?.target?.value })}
              placeholder="Reason for PTO request..."
              rows={2}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground resize-none"
            />
          </div>

          <div>
            <label className="text-xs font-caption text-muted-foreground mb-1 block">Coverage Arrangement</label>
            <input
              type="text"
              value={form?.coverageArrangement}
              onChange={(e) => setForm({ ...form, coverageArrangement: e?.target?.value })}
              placeholder="Who will cover your responsibilities?"
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="default" iconName="Send" loading={submitting}>
              Submit Request
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPTORequestModal;
